import { useCallback, useState } from 'react'
import { ActionButton } from '../Button/ActionButton'
import { Modal, ModalStateProps } from '../Modal/Modal'
import { Checkbox } from './components/Checkbox'
import { Input } from './components/Input'
import { useInputValues } from './UserRegistrationModalForm.hooks'
import { isMailValid } from './UserRegistrationModalForm.utils'

type SubmitDataType = {
  email: string
  phone: string
  name: string
  address: string
  postalCode: string
  postOffice: string
}

interface UserRegistrationModalFormProps extends ModalStateProps {
  onSubmit: (data: SubmitDataType) => Promise<any>
  isGrillQuiz?: boolean
  isSprinten?: boolean
}

export const UserRegistrationModalForm = ({
  isOpen,
  onClose,
  onSubmit,
  isGrillQuiz = false,
  isSprinten = false,
}: UserRegistrationModalFormProps) => {
  const {state, handleChange, resetState, saveStateToLocalStorage} = useInputValues()

  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isNameValid = !!state.name && state.name.trim().length > 1
  const isEmailValid = !!state.email && isMailValid(state.email)
  const isAgreeWithTerms = !!state.termsAgree
  const isValid = isNameValid && isEmailValid && isAgreeWithTerms

  const onRenderedChange = useCallback(() => {
    resetState()
  }, [resetState])

  const handleSubmit = () => {
    setIsSubmitted(true)
    if (!isValid) return

    setIsSubmitting(true)

    onSubmit(state)
      .then(() => saveStateToLocalStorage())
      .finally(() => setIsSubmitting(false))
  }
  const colorInputProps = {
    bgColor: isGrillQuiz ? '#202020' : undefined,
    borderColor: isGrillQuiz ? '#707070' : undefined,
  }

  const modalOverlayColor = isGrillQuiz
    ? 'rgba(38, 56, 67, 0.66)'
    : isSprinten
      ? 'rgba(7, 17, 68, 0.7)'
      : undefined
  const modalBgColor = isGrillQuiz ? '#040100' : isSprinten ? '#071144' : undefined

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onRenderedChange={onRenderedChange}
      overlayColor={modalOverlayColor}
      bgColor={modalBgColor}
      id="registration-modal"
    >
      <div id="reg-title" className="mt-8 mb-2 text-center text-2xl text-white">
        Vinn 500kr i gavekort!
      </div>
      <div id="reg-description" className="mb-5 text-center text-base font-medium text-white">
        {isSprinten
          ? '4 vinnere trekkes hver måned. Jo flere ganger du spiller, desto større er vinnersjansene'
          : 'Vinnere trekkes en gang i måneden. Jo flere ganger du spiller, desto større er vinnersjansene'}
      </div>
      <div className="mb-[32px] flex flex-col gap-[20px]">
        <Input
          idLabel="reg-name-text"
          idInput="reg-name-input"
          label="Navn"
          value={state.name}
          onChange={e => handleChange('name', e.target.value)}
          error={isSubmitted && !state.name && 'Påkrevd'}
          required
          {...colorInputProps}
        />
        <Input
          idLabel="reg-email-text"
          idInput="reg-email-input"
          label="E-post"
          value={state.email}
          onChange={e => handleChange('email', e.target.value)}
          error={isSubmitted && !isMailValid(state.email) && 'E-post er ikke gyldig'}
          required
          {...colorInputProps}
        />
        <Input
          idLabel="reg-phone-text"
          idInput="reg-phone-input"
          label="Telefonnummer"
          value={state.phone}
          onChange={e => handleChange('phone', e.target.value)}
          type="tel"
          {...colorInputProps}
        />
        <Input
          idLabel="reg-address-text"
          idInput="reg-address-input"
          label="Adresse"
          value={state.address}
          onChange={e => handleChange('address', e.target.value)}
          {...colorInputProps}
        />
        <Input
          idLabel="reg-post-index-text"
          idInput="reg-post-index-input"
          label="Postnummer"
          value={state.postalCode}
          onChange={e => handleChange('postalCode', e.target.value)}
          {...colorInputProps}
        />
        <Input
          idLabel="reg-post-office-text"
          idInput="reg-post-office-input"
          label="Poststed"
          value={state.postOffice}
          onChange={e => handleChange('postOffice', e.target.value)}
          {...colorInputProps}
        />
        <Checkbox
          idLabel="agree-with-terms-text"
          idInput="agree-with-terms-checkbox"
          label="Jeg samtykker til vilkårene"
          checked={state.termsAgree}
          onChange={e => handleChange('termsAgree', e.target.checked)}
          required
          error={isSubmitted && !state.termsAgree}
          labelLink="https://www.coop.no/spill/premier-og-regler"
          {...colorInputProps}
        />
      </div>
      <ActionButton
        id="reg-send-btn"
        isLoading={isSubmitting}
        color={isGrillQuiz ? 'orange' : 'purple'}
        className="bg-red w-full"
        onClick={handleSubmit}
      >
        Send inn
      </ActionButton>
    </Modal>
  )
}
